import React from "react";
import { SparklesIcon, TagIcon, TruckIcon } from "@heroicons/react/24/outline";
import { Button, Input } from "../ui";
import { formatZl, plural, PRODUCT_WORDS } from "../../utils/format";

const PROMO_STATUS = {
  applied: { text: "Промокод применён", className: "text-emerald-400" },
  invalid: { text: "Промокод не найден или уже не действует", className: "text-red-400" },
  not_met: { text: "Условия акции по промокоду пока не выполнены", className: "text-amber-400" },
  not_best: { text: "Уже действует более выгодная акция", className: "text-amber-400" },
};

const Row = ({ label, children, className = "text-slate-400" }) => (
  <div className={`flex justify-between gap-4 ${className}`}>
    <span>{label}</span>
    {children}
  </div>
);

/**
 * Блок «Ваш заказ»: промокод, суми з серверного розрахунку (quote), підказки
 * до безкоштовної доставки й до акції.
 */
const OrderSummary = ({
  quote,
  itemsCount,
  subtotal,
  discount,
  deliveryPrice,
  totalPrice,
  selectedDelivery,
  promoInput,
  setPromoInput,
  appliedCode,
  setAppliedCode,
}) => {
  // Статус показуємо лише для коду, з яким прийшла поточна відповідь
  const promoStatus =
    appliedCode && quote?.promo_code === appliedCode ? PROMO_STATUS[quote.promo_code_status] : null;

  const freeFrom = Number(selectedDelivery?.free_from) || 0;
  const freeLeft = Number(quote?.delivery_free_left) || 0;

  const hint = quote?.hint;
  const hintText = hint
    ? [
        hint.missing_quantity > 0 &&
          `${hint.missing_quantity} ${plural(hint.missing_quantity, PRODUCT_WORDS)}`,
        Number(hint.missing_amount) > 0 && `товаров на ${formatZl(hint.missing_amount)}`,
      ]
        .filter(Boolean)
        .join(" и ")
    : "";

  const applyPromo = () => setAppliedCode(promoInput.trim());
  const isApplied = appliedCode && appliedCode === promoInput.trim();

  return (
    <section className="p-5 sm:p-6 rounded-3xl bg-slate-900/80 border border-slate-700 space-y-4">
      <div className="flex justify-between items-baseline">
        <h2 className="text-lg font-bold">Ваш заказ</h2>
        <span className="text-sm text-blue-400 font-medium">
          {itemsCount} {plural(itemsCount, PRODUCT_WORDS)}
        </span>
      </div>

      {/* PROMO CODE */}
      <div>
        <div className="flex gap-2">
          <Input
            className="flex-1"
            icon={TagIcon}
            value={promoInput}
            onChange={(e) => setPromoInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyPromo()}
            placeholder="Промокод"
            aria-label="Промокод"
            autoCapitalize="characters"
          />
          {isApplied ? (
            <Button
              variant="secondary"
              size="lg"
              className="px-4"
              onClick={() => {
                setPromoInput("");
                setAppliedCode("");
              }}
            >
              Убрать
            </Button>
          ) : (
            <Button size="lg" className="px-4" onClick={applyPromo} disabled={!promoInput.trim()}>
              Применить
            </Button>
          )}
        </div>
        {promoStatus && (
          <p className={`mt-2 text-sm font-medium ${promoStatus.className}`}>{promoStatus.text}</p>
        )}
      </div>

      <div className="space-y-3 pt-1">
        <Row label="Товары">
          <span className="text-slate-200">{formatZl(subtotal)}</span>
        </Row>

        {discount > 0 && (
          <Row
            className="text-emerald-400"
            label={
              <>
                Скидка
                {quote?.promotion && (
                  <span className="block text-xs text-emerald-400/80">{quote.promotion.name}</span>
                )}
              </>
            }
          >
            <span className="font-semibold whitespace-nowrap">−{formatZl(discount)}</span>
          </Row>
        )}

        {selectedDelivery && (
          <Row label="Доставка">
            {deliveryPrice > 0 ? (
              <span className="text-slate-200">{formatZl(deliveryPrice)}</span>
            ) : (
              <span className="text-emerald-400 font-semibold">Бесплатно</span>
            )}
          </Row>
        )}

        {freeLeft > 0 && (
          <div className="rounded-2xl bg-emerald-500/10 border border-emerald-500/25 p-3">
            <div className="flex items-center gap-2 text-sm text-emerald-300">
              <TruckIcon className="w-4 h-4 shrink-0" />
              <span>
                До бесплатной доставки — ещё <b>{formatZl(freeLeft)}</b>
              </span>
            </div>
            {freeFrom > 0 && (
              <div className="mt-2 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className="h-full rounded-full bg-emerald-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, ((freeFrom - freeLeft) / freeFrom) * 100)}%` }}
                />
              </div>
            )}
          </div>
        )}

        {hint && hintText && (
          <div className="flex items-start gap-2 rounded-2xl bg-primary/10 border border-primary/25 p-3 text-sm text-blue-200">
            <SparklesIcon className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Добавьте ещё {hintText} — получите скидку{" "}
              <b>{hint.discount_label.replace(/^-/, "")}</b>
            </span>
          </div>
        )}

        <div className="border-t border-slate-700 pt-4 flex justify-between items-baseline">
          <span className="text-lg font-bold">Итого</span>
          <span className="text-2xl font-black text-blue-400">{formatZl(totalPrice)}</span>
        </div>
      </div>
    </section>
  );
};

export default OrderSummary;
